import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useLazyPayDataBankAccountLookupQuery,
  useLazyPayDataBankLookupQuery,
  useUpdatePayDataMutation,
} from '@/store/api/payDataApi'
import type { PayDataLookupItem } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import {
  compactDialogContentSx,
  dialogHeaderBtnSx,
  dialogTitleActionsSx,
  dialogTitleSx,
  dialogTitleTextSx,
  roSx,
} from '../purchase/dialogStyle'

interface Props {
  open: boolean
  cpCode: string
  supplierLabel: string
  paydataIds: number[]
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

/** Verbatim Access `btnSave_Click` guard messages. */
const BANK_REQUIRED_MSG = 'Please choose Bank!'
const BANK_ACCOUNT_REQUIRED_MSG = 'Please choose Bank Account!'

/**
 * Change Bank dialog — converts Access form `frmProcurementQryPayDataBankAdd`
 * (Procurement Pay Data → Change Bank).
 *
 * Opened for a set of ticked Pay Data rows that all share one supplier
 * (`cpCode`). Lets the user pick a Bank + Bank Account (account cascades from
 * bank, per Access `Load_Source`) then applies `{ bank, bank_account }` to each
 * ticked paydata record on Save.
 *
 * Autocomplete combos inject the current value into options. No fullScreen;
 * mobile stacks to a single column with internal scroll.
 */
export default function PayDataChangeBankDialog({
  open,
  cpCode,
  supplierLabel,
  paydataIds,
  canEdit,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const [updatePayData, { isLoading: isSaving }] = useUpdatePayDataMutation()

  const [fetchBanks, { data: bankData, isFetching: banksLoading }] =
    useLazyPayDataBankLookupQuery()
  const [fetchBankAccounts, { data: accountData, isFetching: accountsLoading }] =
    useLazyPayDataBankAccountLookupQuery()

  const [bank, setBank] = useState<string>('')
  const [bankAccount, setBankAccount] = useState<string>('')
  const [bankError, setBankError] = useState<string>('')
  const [accountError, setAccountError] = useState<string>('')

  // Re-init + load bank options each time the dialog opens.
  useEffect(() => {
    if (!open) return
    setBank('')
    setBankAccount('')
    setBankError('')
    setAccountError('')
    if (cpCode) void fetchBanks({ cpCode })
  }, [open, cpCode, fetchBanks])

  // Cascade: reload bank-account options whenever the picked bank changes
  // (Access `bank_AfterUpdate` → `Load_Source`).
  useEffect(() => {
    if (!open || !cpCode || !bank) return
    void fetchBankAccounts({ cpCode, bank })
  }, [open, cpCode, bank, fetchBankAccounts])

  // Inject the current value into options so a stored value never shows blank.
  const bankOptions = useMemo<PayDataLookupItem[]>(() => {
    const items = bankData?.items ?? []
    if (bank && !items.some((it) => it.value === bank)) {
      return [{ value: bank, label: bank }, ...items]
    }
    return items
  }, [bankData, bank])

  const accountOptions = useMemo<PayDataLookupItem[]>(() => {
    const items = accountData?.items ?? []
    if (bankAccount && !items.some((it) => it.value === bankAccount)) {
      return [{ value: bankAccount, label: bankAccount }, ...items]
    }
    return items
  }, [accountData, bankAccount])

  const bankValue = useMemo(
    () => bankOptions.find((it) => it.value === bank) ?? null,
    [bankOptions, bank],
  )
  const accountValue = useMemo(
    () => accountOptions.find((it) => it.value === bankAccount) ?? null,
    [accountOptions, bankAccount],
  )

  const handleSave = async () => {
    if (!canEdit) return
    if (!bank || bank.trim() === '') {
      setBankError(BANK_REQUIRED_MSG)
      snack.warning(BANK_REQUIRED_MSG)
      return
    }
    if (!bankAccount || bankAccount.trim() === '') {
      setAccountError(BANK_ACCOUNT_REQUIRED_MSG)
      snack.warning(BANK_ACCOUNT_REQUIRED_MSG)
      return
    }
    try {
      for (const id of paydataIds) {
        await updatePayData({ id, body: { bank, bank_account: bankAccount } }).unwrap()
      }
      snack.success('Successfully Updated')
      onSaved()
      onClose()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to update bank for the selected pay data'))
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle sx={dialogTitleSx}>
        <Typography
          component="span"
          noWrap
          title="Inbound Logistics - Change Bank"
          sx={dialogTitleTextSx}
        >
          Inbound Logistics - Change Bank
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            variant="contained"
            size="small"
            onClick={() => void handleSave()}
            disabled={isSaving || !canEdit}
            sx={dialogHeaderBtnSx}
          >
            {isSaving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={onClose}
            disabled={isSaving}
            sx={dialogHeaderBtnSx}
          >
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {`Apply the selected bank to ${paydataIds.length} pay data ${
            paydataIds.length === 1 ? 'record' : 'records'
          } of this supplier.`}
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          }}
        >
          {/* Row 1: Supplier (readonly) — spans full width */}
          <Box sx={{ gridRow: '1', gridColumn: { xs: '1', sm: '1 / span 2' } }}>
            <TextField
              label="Supplier"
              size="small"
              fullWidth
              value={supplierLabel || cpCode || ''}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={roSx}
            />
          </Box>

          {/* Row 2: Bank */}
          <Box sx={{ gridRow: { xs: '2', sm: '2' }, gridColumn: '1' }}>
            <Autocomplete<PayDataLookupItem>
              size="small"
              options={bankOptions}
              loading={banksLoading}
              value={bankValue}
              getOptionLabel={(o) => o.label ?? ''}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              onChange={(_e, val) => {
                setBank(val?.value ?? '')
                // Cascade reset: bank changed → clear dependent account.
                setBankAccount('')
                if (val?.value) setBankError('')
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Bank *"
                  error={!!bankError}
                  helperText={bankError || undefined}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {banksLoading ? <CircularProgress size={14} color="inherit" /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>

          {/* Row 2: Bank Account (cascades from Bank) */}
          <Box sx={{ gridRow: { xs: '3', sm: '2' }, gridColumn: { xs: '1', sm: '2' } }}>
            <Autocomplete<PayDataLookupItem>
              size="small"
              options={accountOptions}
              loading={accountsLoading}
              disabled={!bank}
              value={accountValue}
              getOptionLabel={(o) => o.label ?? ''}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              onChange={(_e, val) => {
                setBankAccount(val?.value ?? '')
                if (val?.value) setAccountError('')
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Bank Account *"
                  error={!!accountError}
                  helperText={accountError || undefined}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {accountsLoading ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
